import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";   // ⚠️ chemin à adapter (même que les autres pages du dossier)
import "./Dashboard.css";

const baseUrl = process.env.REACT_APP_BASE_URL;
const API_DASHBOARD = `${baseUrl}/api/Dashboard`;

const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const list = (res) => { const d = res?.data?.data ?? res?.data; return Array.isArray(d) ? d : Array.isArray(d?.$values) ? d.$values : []; };
const dh = (v) => Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " DH";
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "—");
const ht = (d) => Number(d.totalHT ?? d.totalTTC ?? 0);
const ttc = (d) => Number(d.totalTTC ?? d.totalHT ?? 0);
const dateOf = (d) => (d.dateDocument ? new Date(d.dateDocument) : null);

// Règles : une facture compte dans le CA si elle est validée ; « en attente » = devis non transformé
const estValide = (d) => d.statut === "Validé" || d.statut === "Transféré" || d.statut === "Partiel";
const estPayee = (d) => d.paymentStatusLabel === "Payé";
const estTransfere = (d) => d.statut === "Transféré";

// Champs d'article (les noms varient selon l'API) — ⚠️ ajuster si besoin
const stockOf = (a) => Number(a.stockQuantity ?? a.quantiteStock ?? a.stock ?? a.quantity ?? NaN);
const seuilOf = (a) => Number(a.stockMin ?? a.seuilAlerte ?? a.minStock ?? 0);
const familleOf = (a) => a.familleName ?? a.famille ?? a.familyName ?? a.categoryName ?? a.category ?? "Sans famille";

function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [s, setS] = useState(null);

    useEffect(() => {
        axiosInstance.get(API_DASHBOARD)
            .then((res) => setS(res?.data?.data ?? res?.data))
            .catch(() => setS(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="content-header"><div className="container-fluid"><i className="fas fa-spinner fa-spin mr-2"></i>Chargement…</div></div>;

    if (!s) return <div className="content-header"><div className="container-fluid text-danger">Impossible de charger le tableau de bord.</div></div>;

    const evol = s.caMoisPrec ? Math.round(((s.caMois - s.caMoisPrec) / s.caMoisPrec) * 100) : null;
    const maxMois = Math.max(1, ...s.mois.map((m) => Math.max(m.ca, m.achats)));
    const maxFam = Math.max(1, ...s.parFamille.map((f) => f.montant));
    const maxCli = Math.max(1, ...s.topClients.map((c) => c.ca));

    const Box = ({ color, value, label, sub, icon }) => (
        <div className="col-lg-3 col-md-6 col-sm-12">
            <div className="small-box" style={{ backgroundColor: color, color: "white" }}>
                <div className="inner">
                    <h3>{value}</h3>
                    <p className="mb-0">{label}</p>
                    <small className="dc-sub">{sub}</small>
                </div>
                <div className="icon"><i className={`fas ${icon}`} style={{ opacity: 0.5 }}></i></div>
            </div>
        </div>
    );

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-3">
                        <div className="col-sm-6"><h1 className="m-0 text-dark font-weight-bold">Tableau de Bord</h1></div>
                        <div className="col-sm-6 text-right">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="#">Accueil</a></li>
                                <li className="breadcrumb-item active">Tableau de Bord</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <Box color="#4A90E2" value={dh(s.caMois)} label="Chiffre d'affaires HT (mois)" icon="fa-chart-line"
                            sub={evol === null ? `Achats du mois : ${dh(s.achatsMois)}` : `${evol >= 0 ? "+" : ""}${evol} % vs mois dernier`} />
                        <Box color="#50B66D" value={s.devisAttente} label="Devis en attente" icon="fa-file-invoice"
                            sub={`${dh(s.devisMontant)} TTC • ${s.cmdOuvertes} commande(s) à livrer`} />
                        <Box color="#F5A623" value={s.impayees} label="Factures impayées" icon="fa-exclamation-circle"
                            sub={`${dh(s.impayeesMontant)} TTC à encaisser`} />
                        <Box color="#D0021B" value={s.ruptures.length} label="Articles en rupture" icon="fa-box-open"
                            sub={s.articlesSuivis ? `sur ${s.articlesSuivis} articles suivis` : "stock non disponible"} />
                    </div>

                    <div className="row">
                        <div className="col-12 mb-3">
                            <div className="card shadow-sm dc-card">
                                <div className="card-header font-weight-bold d-flex justify-content-between align-items-center">
                                    <span>Encaissements du mois par mode de règlement — {dh(s.totalEncaisse)}</span>
                                    {s.nonAffecte > 0 && <span className="badge badge-warning">{dh(s.nonAffecte)} non affectés à une facture</span>}
                                </div>
                                <div className="card-body d-flex flex-wrap">
                                    {s.reglementsMois.length === 0 && <span className="text-muted">Aucun encaissement ce mois.</span>}
                                    {s.reglementsMois.map((r) => (
                                        <div key={r.mode} className="dc-reg">
                                            <div className="dc-reg-mode">{r.mode}</div>
                                            <div className="dc-reg-montant">{dh(r.montant)}</div>
                                            <div className="dc-reg-n">{r.n} règlement(s)</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-2">
                        <div className="col-lg-7 col-md-12 mb-3">
                            <div className="card shadow-sm dc-card h-100">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <span className="font-weight-bold">Chiffre d'affaires et achats — 12 mois (HT)</span>
                                    <span className="dc-legend"><i className="dc-dot" style={{ background: "#4A90E2" }}></i>CA
                                        <i className="dc-dot ml-3" style={{ background: "#eb6834" }}></i>Achats</span>
                                </div>
                                <div className="card-body">
                                    <div className="dc-cols">
                                        {s.mois.map((m) => (
                                            <div key={m.key} className="dc-col" title={`${m.label} ${m.annee}\nCA : ${dh(m.ca)}\nAchats : ${dh(m.achats)}`}>
                                                <div className="dc-pair">
                                                    <div className="dc-bar" style={{ height: `${(m.ca / maxMois) * 100}%`, background: "#4A90E2" }}></div>
                                                    <div className="dc-bar" style={{ height: `${(m.achats / maxMois) * 100}%`, background: "#eb6834" }}></div>
                                                </div>
                                                <span className="dc-col-lbl">{m.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-5 col-md-12 mb-3">
                            <div className="card shadow-sm dc-card h-100">
                                <div className="card-header font-weight-bold">Ventes par famille d'articles (12 mois)</div>
                                <div className="card-body">
                                    {s.parFamille.length === 0 && <p className="text-muted mb-0">Aucune vente facturée sur la période.</p>}
                                    {s.parFamille.map((f) => (
                                        <div key={f.nom} className="dc-hbar">
                                            <div className="dc-hbar-lbl" title={f.nom}>{f.nom}</div>
                                            <div className="dc-hbar-track"><div className="dc-hbar-fill" style={{ width: `${(f.montant / maxFam) * 100}%` }}></div></div>
                                            <div className="dc-hbar-val">{dh(f.montant)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-4 col-md-12 mb-3">
                            <div className="card shadow-sm dc-card h-100">
                                <div className="card-header font-weight-bold">Top clients (12 mois)</div>
                                <div className="card-body">
                                    {s.topClients.length === 0 && <p className="text-muted mb-0">Aucune facture validée.</p>}
                                    {s.topClients.map((c, i) => (
                                        <div key={c.nom} className="dc-hbar">
                                            <div className="dc-hbar-lbl" title={c.nom}><span className="dc-rank">{i + 1}</span>{c.nom}</div>
                                            <div className="dc-hbar-track"><div className="dc-hbar-fill" style={{ width: `${(c.ca / maxCli) * 100}%`, background: "#50B66D" }}></div></div>
                                            <div className="dc-hbar-val">{dh(c.ca)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12 mb-3">
                            <div className="card shadow-sm dc-card h-100">
                                <div className="card-header font-weight-bold">Factures à encaisser (les plus anciennes)</div>
                                <ul className="list-group list-group-flush">
                                    {s.aEncaisser.length === 0 && <li className="list-group-item text-muted">Aucune facture impayée.</li>}
                                    {s.aEncaisser.map((f) => (
                                        <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                            <span className="text-truncate mr-2">
                                                <strong>{f.numero}</strong> <small className="text-muted">{fmtDate(f.dateDocument)}</small>
                                                <small className="d-block text-muted text-truncate">{f.clientName}</small>
                                            </span>
                                            <span className="text-nowrap">
                                                {dh(ttc(f))}
                                                {f.paymentStatusLabel === "Partiellement payé" && <span className="badge badge-warning ml-1">Partiel</span>}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12 mb-3">
                            <div className="card shadow-sm dc-card h-100">
                                <div className="card-header font-weight-bold">Articles en rupture / sous le seuil</div>
                                <ul className="list-group list-group-flush">
                                    {s.ruptures.length === 0 && <li className="list-group-item text-muted">
                                        {s.articlesSuivis ? "Aucun article en rupture." : "Le stock n'est pas renvoyé par l'API Articles."}</li>}
                                    {s.ruptures.slice(0, 6).map((a) => (
                                        <li key={a.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                            <span className="text-truncate mr-2">{a.nameLat || a.name || a.code}</span>
                                            <span className={`badge ${stockOf(a) <= 0 ? "badge-danger" : "badge-warning"}`}>Stock : {stockOf(a)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Dashboard;
